import { ICard } from "../deckAPI"
import { EffectTarget, IEffectType } from "../effectsAPI"

export const enum GAMEPLAY_SOCKETS {
  GAMEPLAY_STATE = "state",
  GAMEPLAY_EVENT = "event"
}

export const GAMEPLAY_PHASE = {
  TURN_START: "TURN_START",
  ACTION: "ACTION",
  RESOLVING: "RESOLVING",
  REFILL: "REFILL",
  TURN_END: "TURN_END"
} as const

export type IGameplayPhase =
  (typeof GAMEPLAY_PHASE)[keyof typeof GAMEPLAY_PHASE]

export interface IGameplayStatePlayer {
  player: string
  progress: number
  mirroring: number
  speedBonus: number
  block: number
  speed: number
  luck: number
  stamina: number
  deltaSpeed: number
  deltaLuck: number
  deltaStamina: number
  skipDiggingMovements: number
  freezingMovements: number
}

export interface IGameplayStateRestrictionItem {
  playCount: number
  cooldownEndsAtTurn: number
}

export interface IGameplayStateResponse {
  sessionId: string
  phase: IGameplayPhase
  turn: number
  currentPlayerId: string
  currentCard: ICard | null
  prevCard: ICard | null
  players: Record<string, IGameplayStatePlayer>
  version: number
  deckSize: number
  turnStartedAt: string
  moveTimeMs: number
  currentCardPlayedBy?: string
  prevCardPlayedBy?: string
  cardRestrictions: Record<string, IGameplayStateRestrictionItem>
}

export const GAMEPLAY_CARD_ACTION = {
  PLAY: "PLAY",
  DISCARD: "DISCARD",
  SKIP: "SKIP"
} as const

export type IGameplayCardAction =
  (typeof GAMEPLAY_CARD_ACTION)[keyof typeof GAMEPLAY_CARD_ACTION]

export interface IGameplayActionRequest {
  sessionId: string
  cardId: string | null
  action: IGameplayCardAction
}

export interface IGameplayStateEvent extends IGameplayStateResponse {}

export interface IGameplayEventResponse {}

export const GAMEPLAY_EVENT_FROM = {
  DECK: "DECK",
  TABLE: "TABLE",
  GRAVEYARD: "GRAVEYARD"
} as const

export type IGameplayEventFrom =
  (typeof GAMEPLAY_EVENT_FROM)[keyof typeof GAMEPLAY_EVENT_FROM]

export interface IGameplayEvent {
  sessionId: string
  from: IGameplayEventFrom | string
  to: IGameplayEventFrom | string
  card: ICard | null // card id
}

// TODO: Add gameplay log types

export interface IGameplayLogBase<TType extends string, TDetails> {
  _id?: string
  sessionId: string
  type: TType
  currentPlayer: string
  turn: number
  details: TDetails
  createdAt: string
}

export type IPlayerTargetLog = Exclude<EffectTarget, EffectTarget.NONE>
export interface ICardEffectLog {
  _id: string
  type: IEffectType
  value: number
  duration: number
  target: IPlayerTargetLog
}

// CARD_PLAYED
export interface ICardPlayedDetails {
  cardId: string
  cardType: string
  cardSubtype: string
  cardEffects: ICardEffectLog[]
}

export type ICardPlayedLog = IGameplayLogBase<"CARD_PLAYED", ICardPlayedDetails>

// EFFECT_ACTIVATED
export interface IEffectActivatedDetails {
  probabilityPercentage: number
  isApplied: boolean
  effectType: IEffectType
  duration: number
  playerTarget: IPlayerTargetLog
  remainsActive: boolean
}

export type IEffectActivatedLog = IGameplayLogBase<
  "EFFECT_ACTIVATED",
  IEffectActivatedDetails
>

// EFFECT_APPLIED
export interface IStatChange {
  before: number
  after: number
}

type IKnownChangeKey =
  | "progress"
  | "block"
  | "speedBonus"
  | "deltaStamina"
  | "deltaSpeed"
  | "deltaLuck"
  | "skipDiggingMovements"
  | "freezingMovements"
  | "stamina"
  | "speed"
  | "luck"
  | "mirroring"

export type IEffectChanges = Partial<Record<IKnownChangeKey, IStatChange>> &
  Record<string, IStatChange | undefined>

export interface IEffectAppliedDetails {
  effectId: string
  targetPlayerId: string
  playerTarget: IPlayerTargetLog
  effectType: IEffectType
  changes: IEffectChanges
  meta: Record<string, unknown>
  remainsActive: boolean
}

export type IEffectAppliedLog = IGameplayLogBase<
  "EFFECT_APPLIED",
  IEffectAppliedDetails
>

// DECK_RESHUFFLED
export interface IDeckReshuffledDetails {
  newDeckSize: number
}

export type IDeckReshuffledLog = IGameplayLogBase<
  "DECK_RESHUFFLED",
  IDeckReshuffledDetails
>

// CARD_DISCARDED
export interface ICardDiscardedDetails {
  cardId: string
  type: string
}

export type ICardDiscardedLog = IGameplayLogBase<
  "CARD_DISCARDED",
  ICardDiscardedDetails
>

// SKIP_REGISTERED
export const SKIP_REASON = {
  AUTO_SKIP: "auto_skip",
  USER_PRESSED_SKIP: "user_pressed_skip"
} as const

export type ISkipReason = (typeof SKIP_REASON)[keyof typeof SKIP_REASON]
export interface ISkipRegisteredDetails {
  reason: ISkipReason
}

export type ISkipRegisteredLog = IGameplayLogBase<
  "SKIP_REGISTERED",
  ISkipRegisteredDetails
>

// GAME_OVER
export type IGameOverLog = IGameplayLogBase<"GAME_OVER", {}>

// GAME_START
export type IGameStartLog = IGameplayLogBase<"GAME_START", {}>

export type IGameplayLog =
  | ICardPlayedLog
  | IEffectActivatedLog
  | IEffectAppliedLog
  | IDeckReshuffledLog
  | ICardDiscardedLog
  | ISkipRegisteredLog
  | IGameOverLog
  | IGameStartLog
